/**
 * GTFS route colors are free-form: a very light color would leave the trip
 * timeline, drawn on the white sheet, close to invisible. `timelineColor`
 * darkens such colors until they reach a minimal contrast and keeps every
 * other color untouched.
 */

const DEFAULT_LINE_COLOR = '#333333';

// Relative luminance above which a color is too light for a white background
const MAX_LUMINANCE = 0.7;
const DARKEN_FACTOR = 0.8;

function parseHex(color: string): [number, number, number] | null {
  const hex = color.replace(/^#/, '');
  if (!/^[0-9a-f]{6}$/i.test(hex)) return null;
  return [parseInt(hex.slice(0, 2), 16), parseInt(hex.slice(2, 4), 16), parseInt(hex.slice(4, 6), 16)];
}

function toHex(rgb: [number, number, number]): string {
  return `#${rgb.map(c => Math.min(255, Math.max(0, c)).toString(16).padStart(2, '0')).join('')}`;
}

/** WCAG relative luminance, 0 (black) to 1 (white). */
function luminance(rgb: [number, number, number]): number {
  const [r, g, b] = rgb.map(c => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Color of the trip timeline for a route: the GTFS `route_color` (with or
 * without a leading `#`), darkened when too light to read on white.
 */
export function timelineColor(routeColor?: string): string {
  if (!routeColor) return DEFAULT_LINE_COLOR;
  let rgb = parseHex(routeColor);
  if (!rgb) return DEFAULT_LINE_COLOR;
  // Each step keeps the hue; the guard caps pathological inputs like #ffffff
  let guard = 10;
  while (luminance(rgb) > MAX_LUMINANCE && guard-- > 0) {
    rgb = rgb.map(c => Math.round(c * DARKEN_FACTOR)) as [number, number, number];
  }
  return toHex(rgb);
}

/** The already-travelled segment of the timeline: the line color washed out toward white. */
export function fadedTimelineColor(color: string): string {
  const rgb = parseHex(color) ?? (parseHex(DEFAULT_LINE_COLOR) as [number, number, number]);
  return toHex(rgb.map(c => Math.round(c * 0.4 + 255 * 0.6)) as [number, number, number]);
}
